import type { ReactNode } from 'react';
import { Alert, type AlertLive } from '../../atoms/Alert/Alert';
import { Button } from '../../atoms/Button/Button';
import { useTranslation } from '../../../i18n/useTranslation';
import './LoadErrorState.css';

export interface LoadErrorStateProps {
  /** Translated headline, e.g. t('alerts.errorTitle'). */
  title: ReactNode;
  /** Render the title as a heading of this level (see Alert `titleLevel`). */
  titleLevel?: 2 | 3 | 4 | 5 | 6;
  /** Optional translated explanation / next step. Never raw backend text. */
  message?: ReactNode;
  /** Retry handler (e.g. a query's refetch). Without it no button is rendered. */
  onRetry?: () => void;
  /** Translated label for the retry button. Defaults to t('common.retry'). */
  retryLabel?: string;
  /** Shows the retry button in its loading state while a retry is running. */
  retrying?: boolean;
  /** Announcement politeness, see Alert. Defaults to Alert's danger default (assertive). */
  live?: AlertLive;
  /** Tighter variant for load errors inside a card or a section. */
  compact?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * LoadErrorState — the page/section-level "could not load" state.
 *
 * A composition of the Alert atom (danger tone, icon + text, role="alert") and a
 * secondary "Retry" Button. Use it wherever a list, page or section failed to
 * load; form submit errors and inline action errors use <Alert> directly.
 */
export function LoadErrorState({
  title,
  titleLevel,
  message,
  onRetry,
  retryLabel,
  retrying = false,
  live,
  compact = false,
  className = '',
  'data-testid': testId,
}: LoadErrorStateProps) {
  const { t } = useTranslation();
  return (
    <Alert
      tone="danger"
      title={title}
      titleLevel={titleLevel}
      live={live}
      compact={compact}
      className={['rg-load-error-state', className].filter(Boolean).join(' ')}
      data-testid={testId}
      action={
        onRetry ? (
          <Button variant="secondary" size="sm" onClick={onRetry} isLoading={retrying}>
            {retryLabel ?? t('common.retry')}
          </Button>
        ) : undefined
      }
    >
      {message}
    </Alert>
  );
}

export default LoadErrorState;
